import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react';
import {
  FaImage,
  FaLayerGroup,
  FaSearch,
  FaTh,
  FaThLarge
} from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import PageLayout from './PageLayout';

const mosaicPattern = [
  { colSpan: { base: 2, md: 2 }, rowSpan: { base: 1, md: 2 }, height: { base: '180px', md: '280px' } },
  { colSpan: { base: 1, md: 1 }, rowSpan: { base: 1, md: 1 }, height: { base: '150px', md: '180px' } },
  { colSpan: { base: 1, md: 1 }, rowSpan: { base: 1, md: 1 }, height: { base: '150px', md: '180px' } },
  { colSpan: { base: 2, md: 2 }, rowSpan: { base: 1, md: 1 }, height: { base: '160px', md: '200px' } },
  { colSpan: { base: 1, md: 1 }, rowSpan: { base: 1, md: 2 }, height: { base: '180px', md: '260px' } },
  { colSpan: { base: 1, md: 1 }, rowSpan: { base: 1, md: 1 }, height: { base: '150px', md: '180px' } }
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const isImageAsset = (filename) => typeof filename === 'string' && /\.(jpe?g|png|gif|webp|bmp)$/i.test(filename);

const naturalSort = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

const buildGalleryUrl = (galleryId, filename) => `${API_BASE}/Galerias/${galleryId}/${filename}`;

const MemoriasFotograficasPage = () => {
  const [galerias, setGalerias] = useState({});
  const [selectedYear, setSelectedYear] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const loadGalleries = async () => {
      try {
        const response = await axios.get(`${API_BASE}/getGaleriaInstiuticao?id_insituicao=1`);
        const galeriasData = response.data.result?.[0] || [];
        const enriched = await Promise.all(
          galeriasData.map(async (galeria) => {
            let cover = null;
            try {
              const fotosResponse = await fetch(`${API_BASE}/getFotosGaleria?id_galeria=${galeria.id_galeria}`);
              if (fotosResponse.ok) {
                const fotosData = await fotosResponse.json();
                const sorted = (fotosData.fotos || [])
                  .filter(isImageAsset)
                  .sort(naturalSort);
                const firstPhoto = sorted[0];
                if (firstPhoto) {
                  cover = buildGalleryUrl(galeria.id_galeria, firstPhoto);
                }
              }
            } catch (fetchError) {
              console.error('Erro ao buscar foto de capa:', fetchError);
            }
            if (!cover && galeria.capa && isImageAsset(galeria.capa)) {
              cover = buildGalleryUrl(galeria.id_galeria, galeria.capa);
            }
            if (!cover && galeria.preview && isImageAsset(galeria.preview)) {
              cover = buildGalleryUrl(galeria.id_galeria, galeria.preview);
            }
            return { ...galeria, cover };
          })
        );
        const groupedByYear = enriched.reduce((acc, galeria) => {
          const year = new Date(galeria.data).getFullYear();
          if (!acc[year]) {
            acc[year] = [];
          }
          acc[year].push(galeria);
          return acc;
        }, {});
        setGalerias(groupedByYear);
      } catch (error) {
        console.error('Erro ao buscar galerias:', error);
        toast({
          title: 'Erro',
          description: 'Nao foi possivel carregar as galerias.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    };

    loadGalleries();
  }, [toast]);

  const years = useMemo(() => (
    Object.keys(galerias)
      .map((year) => Number(year))
      .sort((a, b) => b - a)
  ), [galerias]);

  const totalGalerias = useMemo(() => (
    Object.values(galerias).reduce((acc, list) => acc + list.length, 0)
  ), [galerias]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredGalerias = useMemo(() => {
    if (!selectedYear) return [];
    const list = galerias[selectedYear] || [];
    if (!normalizedQuery) return list;
    return list.filter((gallery) => {
      const texto = `${gallery.titulo || ''} ${gallery.descricao || ''}`.toLowerCase();
      return texto.includes(normalizedQuery);
    });
  }, [galerias, normalizedQuery, selectedYear]);

  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSearchQuery('');
  };

  const handleGalleryClick = (gallery) => {
    navigate(`/memorias-fotograficas/${gallery.id_galeria}`, { state: { gallery } });
  };

  const getGalleryCoverUrl = (gallery) => {
    if (!gallery) return null;
    return gallery.cover ||
      (gallery.capa ? `${API_BASE}/Galerias/${gallery.id_galeria}/${gallery.capa}` : null) ||
      (gallery.preview ? `${API_BASE}/Galerias/${gallery.id_galeria}/${gallery.preview}` : null);
  };

  return (
    <PageLayout
      title="Memórias Fotográficas"
      subtitle="Galerias"
      onBack={() => navigate('/')}
      accent="#93C8E6"
      background="#f5f7fb"
      watermark="/maconaria.png"
    >
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
        <Box bg="white" borderRadius="22px" p={5} boxShadow="0 12px 30px rgba(15,23,42,0.12)">
          <HStack spacing={3} mb={3}>
            <Box
              width="44px"
              height="44px"
              borderRadius="14px"
              bg="rgba(147,200,230,0.5)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="blue.700"
            >
              <FaLayerGroup />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">Total de galerias</Text>
              <Text fontWeight="bold" color="gray.800">{totalGalerias}</Text>
            </Box>
          </HStack>
          <Text fontSize="sm" color="gray.500">Selecione um ano para mergulhar nas memorias.</Text>
        </Box>
        <Box bg="white" borderRadius="22px" p={5} boxShadow="0 12px 30px rgba(15,23,42,0.12)">
          <Text fontSize="sm" color="gray.500">Ano selecionado</Text>
          <Text fontSize="2xl" fontWeight="bold" color="gray.800">
            {selectedYear ? selectedYear : 'Todos'}
          </Text>
          {selectedYear && (
            <Badge colorScheme="blue" variant="subtle" mt={2}>
              {galerias[selectedYear]?.length || 0} galerias
            </Badge>
          )}
        </Box>
      </SimpleGrid>

      {!selectedYear && (
        <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={5} position="relative" zIndex={1}>
          {years.map((year) => (
            <Box
              key={year}
              bg="white"
              borderRadius="22px"
              p={4}
              boxShadow="0 14px 30px rgba(15,23,42,0.12)"
              cursor="pointer"
              transition="all 0.2s ease"
              _hover={{ transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(15,23,42,0.15)' }}
              onClick={() => handleYearClick(year)}
            >
              <HStack spacing={3} align="center">
                <Box
                  width="48px"
                  height="48px"
                  borderRadius="16px"
                  bg="rgba(147,200,230,0.5)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="blue.700"
                >
                  <FaImage />
                </Box>
                <Box>
                  <Text fontWeight="bold" color="gray.800">{year}</Text>
                  <Text fontSize="sm" color="gray.500">{galerias[year]?.length || 0} galerias</Text>
                </Box>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {selectedYear && (
        <Stack spacing={5} position="relative" zIndex={1}>
          <Button
            onClick={() => setSelectedYear(null)}
            alignSelf="flex-start"
            variant="ghost"
            color="gray.600"
          >
            Voltar para anos
          </Button>

          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
            bg="white"
            borderRadius="20px"
            p={4}
            boxShadow="0 12px 30px rgba(15,23,42,0.12)"
          >
            <InputGroup maxW={{ base: '100%', md: '320px' }}>
              <InputLeftElement pointerEvents="none">
                <FaSearch color="#94A3B8" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por titulo ou descricao"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                border="none"
                bg="gray.50"
                borderRadius="14px"
              />
            </InputGroup>
            <HStack spacing={3}>
              <Button
                leftIcon={<FaTh />}
                variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                colorScheme="blue"
                onClick={() => setViewMode('grid')}
              >
                Grade
              </Button>
              <Button
                leftIcon={<FaThLarge />}
                variant={viewMode === 'mosaic' ? 'solid' : 'ghost'}
                colorScheme="blue"
                onClick={() => setViewMode('mosaic')}
              >
                Mosaico
              </Button>
            </HStack>
          </Flex>

          {filteredGalerias.length === 0 ? (
            <Box bg="white" borderRadius="22px" p={8} boxShadow="0 12px 30px rgba(15,23,42,0.12)">
              <Text color="gray.500">Nenhuma galeria encontrada.</Text>
            </Box>
          ) : viewMode === 'grid' ? (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
              {filteredGalerias.map((gallery) => {
                const coverUrl = getGalleryCoverUrl(gallery);
                return (
                  <Box
                    key={gallery.id_galeria}
                    bg="white"
                    borderRadius="22px"
                    p={5}
                    boxShadow="0 14px 30px rgba(15,23,42,0.12)"
                    cursor="pointer"
                    transition="all 0.2s ease"
                    _hover={{ transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(15,23,42,0.15)' }}
                    onClick={() => handleGalleryClick(gallery)}
                  >
                    {coverUrl ? (
                      <Box
                        borderRadius="18px"
                        overflow="hidden"
                        mb={3}
                        height="180px"
                        position="relative"
                      >
                        <Image
                          src={coverUrl}
                          alt={`Capa ${gallery.titulo}`}
                          objectFit="cover"
                          width="100%"
                          height="100%"
                        />
                        <Box
                          position="absolute"
                          inset={0}
                          bg="linear-gradient(180deg, rgba(15,23,42,0) 50%, rgba(15,23,42,0.65) 100%)"
                        />
                      </Box>
                    ) : (
                      <Box
                        width="100%"
                        height="180px"
                        mb={3}
                        borderRadius="18px"
                        bg="rgba(147,200,230,0.25)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <FaImage size={32} color="#2B6CB0" />
                      </Box>
                    )}
                    <HStack spacing={3} mb={3}>
                      <Box
                        width="46px"
                        height="46px"
                        borderRadius="16px"
                        bg="rgba(147,200,230,0.5)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color="blue.700"
                      >
                        <FaImage />
                      </Box>
                      <Box>
                        <Text fontWeight="bold" color="gray.800">{gallery.titulo}</Text>
                        <Text fontSize="sm" color="gray.500">{formatDate(gallery.data)}</Text>
                      </Box>
                    </HStack>
                  </Box>
                );
              })}
            </SimpleGrid>
          ) : (
            <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
              {filteredGalerias.map((gallery, index) => {
                const pattern = mosaicPattern[index % mosaicPattern.length];
                const coverUrl = getGalleryCoverUrl(gallery);
                return (
                  <GridItem
                    key={gallery.id_galeria}
                    colSpan={pattern.colSpan}
                    rowSpan={pattern.rowSpan}
                    bg="white"
                    borderRadius="22px"
                    overflow="hidden"
                    boxShadow="0 14px 30px rgba(15,23,42,0.12)"
                    cursor="pointer"
                    onClick={() => handleGalleryClick(gallery)}
                    transition="all 0.2s ease"
                    _hover={{ transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(15,23,42,0.15)' }}
                  >
                    {coverUrl ? (
                      <Box height={pattern.height} position="relative">
                        <Image
                          src={coverUrl}
                          alt={`Capa ${gallery.titulo}`}
                          objectFit="cover"
                          width="100%"
                          height="100%"
                        />
                        <Box
                          position="absolute"
                          inset={0}
                          bg="linear-gradient(180deg, rgba(15,23,42,0) 55%, rgba(15,23,42,0.7) 100%)"
                        />
                      </Box>
                    ) : (
                      <Box height={pattern.height} bg="rgba(147,200,230,0.2)">
                        <Flex align="center" justify="center" height="100%">
                          <FaImage color="#2B6CB0" size="32px" />
                        </Flex>
                      </Box>
                    )}
                    <Box p={4}>
                      <Text fontWeight="bold" color="gray.800">{gallery.titulo}</Text>
                      <Text fontSize="sm" color="gray.500">{formatDate(gallery.data)}</Text>
                    </Box>
                  </GridItem>
                );
              })}
            </Grid>
          )}
        </Stack>
      )}
    </PageLayout>
  );
};

export default MemoriasFotograficasPage;
